import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Reply as ReplyIcon } from '@mui/icons-material';

const bounce = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(4px); }
`;

const SwipeWrapper = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
});

const SwipeContent = styled(Box)(({ swipeOffset, isSwiping }) => ({
  position: 'relative',
  width: '100%',
  transform: `translateX(${swipeOffset}px)`,
  transition: isSwiping ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'grab',
  userSelect: 'none',
  zIndex: 1,
}));

const ReplyIndicator = styled(Box)(({ theme, progress, visible }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: `${progress * 100}%`,
  minWidth: '70px',
  maxWidth: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  background: visible 
    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`
    : 'transparent',
  borderRadius: '12px 0 0 12px',
  transition: 'background 0.15s ease',
}));

const ReplyIndicatorContent = styled(Box)(({ theme, visible }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.2s ease',

  '& .MuiSvgIcon-root': {
    fontSize: '22px',
    color: theme.palette.primary.main,
    animation: visible ? `${bounce} 0.6s ease-in-out infinite` : 'none',
  },
}));

const ReplyText = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const SwipeableMessage = ({ children, onReply, disabled = false, messageData }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const startXRef = useRef(0);
  const hasTriggeredRef = useRef(false);
  const isSwipingRef = useRef(false);
  const swipeOffsetRef = useRef(0);
  const messageDataRef = useRef(messageData);

  messageDataRef.current = messageData;

  const MAX_SWIPE = 120;
  const TRIGGER_THRESHOLD = 80;

  const triggerReply = useCallback(() => {
    if (onReply && messageDataRef.current) {
      onReply(messageDataRef.current);
    }
  }, [onReply]);

  const handleStart = useCallback((clientX) => {
    if (disabled) return;
    setIsSwiping(true);
    isSwipingRef.current = true;
    startXRef.current = clientX;
    hasTriggeredRef.current = false;
  }, [disabled]);

  const handleMove = useCallback((clientX) => {
    if (disabled || !isSwipingRef.current) return;

    const diff = clientX - startXRef.current;

    if (diff > 0) {
      const offset = Math.min(diff, MAX_SWIPE);
      swipeOffsetRef.current = offset;
      setSwipeOffset(offset);

      if (offset >= TRIGGER_THRESHOLD && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        if (navigator.vibrate) {
          navigator.vibrate(15);
        }
      }
    }
  }, [disabled]);

  const handleEnd = useCallback(() => {
    if (disabled) return;
    
    setIsSwiping(false);
    isSwipingRef.current = false;

    if (swipeOffsetRef.current >= TRIGGER_THRESHOLD) {
      triggerReply();
    }

    setSwipeOffset(0);
    swipeOffsetRef.current = 0;
  }, [disabled, triggerReply]);

  const handleTouchStart = (e) => {
    e.stopPropagation();
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    e.stopPropagation();
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    handleEnd();
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    handleStart(e.clientX);
    
    const handleMouseMove = (moveEvent) => {
      moveEvent.stopPropagation();
      handleMove(moveEvent.clientX);
    };

    const handleMouseUp = (upEvent) => {
      upEvent.stopPropagation();
      handleEnd();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const progress = Math.min(swipeOffset / TRIGGER_THRESHOLD, 1);
  const isVisible = swipeOffset > 15;

  return (
    <SwipeWrapper>
      <ReplyIndicator progress={progress} visible={isVisible}>
        <ReplyIndicatorContent visible={isVisible}>
          <ReplyIcon />
          <ReplyText>Reply</ReplyText>
        </ReplyIndicatorContent>
      </ReplyIndicator>

      <SwipeContent
        swipeOffset={swipeOffset}
        isSwiping={isSwiping}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        {children}
      </SwipeContent>
    </SwipeWrapper>
  );
};

export default SwipeableMessage;
